import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import QRScanner from '../components/QRScanner';
import {
  isIOSDevice,
  isAndroidDevice,
  isMobileDevice,
  isHTTPS,
  checkCameraPermissionIOS,
  getIOSMediaPolicy
} from '../utils/iosHelpers';

const MobileTest = () => {
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({});
  const [cameraStatus, setCameraStatus] = useState('checking');
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    // Cihaz bilgilerini topla
    const info = {
      isIOS: isIOSDevice(),
      isAndroid: isAndroidDevice(),
      isMobile: isMobileDevice(),
      isHTTPS: isHTTPS(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      hardwareConcurrency: navigator.hardwareConcurrency,
      maxTouchPoints: navigator.maxTouchPoints,
      mediaDevices: !!navigator.mediaDevices,
      getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      permissions: !!navigator.permissions,
      serviceWorker: 'serviceWorker' in navigator,
      webGL: !!window.WebGLRenderingContext,
      webAudio: !!window.AudioContext,
      webRTC: !!window.RTCPeerConnection
    };
    setDeviceInfo(info);

    // Kamera durumunu kontrol et
    checkCameraStatus();

    // Test sonuçlarını topla
    runTests();
  }, []);

  const checkCameraStatus = async () => {
    try {
      setCameraStatus('checking');
      await checkCameraPermissionIOS();
      setCameraStatus('available');
    } catch (error) {
      setCameraStatus('unavailable');
      console.error('Kamera durumu:', error.message);
    }
  };

  const runTests = () => {
    const results = {
      // Temel özellikler
      touch: 'ontouchstart' in window,
      orientation: 'orientation' in window,
      vibration: 'vibrate' in navigator,
      geolocation: 'geolocation' in navigator,
      
      // Medya özellikleri
      audio: !!window.AudioContext,
      video: !!window.HTMLVideoElement,
      canvas: !!window.HTMLCanvasElement,
      
      // Modern API'ler
      fetch: !!window.fetch,
      promises: !!window.Promise,
      asyncAwait: (async () => {}).constructor.name === 'AsyncFunction',
      
      // Depolama
      localStorage: !!window.localStorage,
      sessionStorage: !!window.sessionStorage,
      indexedDB: !!window.indexedDB,
      
      // Ağ
      webSocket: !!window.WebSocket,
      xhr: !!window.XMLHttpRequest,
      
      // Güvenlik
      crypto: !!window.crypto,
      subtle: !!(window.crypto && window.crypto.subtle)
    };

    setTestResults(results);
  };

  const getStatusIcon = (status) => {
    if (status === true || status === 'available') return '✅';
    if (status === false || status === 'unavailable') return '❌';
    if (status === 'checking') return '⏳';
    return '❓';
  };

  const getStatusText = (status) => {
    if (status === true || status === 'available') return 'Destekleniyor';
    if (status === false || status === 'unavailable') return 'Desteklenmiyor';
    if (status === 'checking') return 'Kontrol ediliyor...';
    return 'Bilinmiyor';
  };

  const handleQRScan = (qrData) => {
    alert(`QR Kod tarandı: ${qrData}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">📱 Mobil Cihaz Test Sayfası</h1>
          <p className="text-gray-600">Cihazınızın özelliklerini ve uyumluluğunu test edin</p>
        </div>

        {/* Navigation */}
        <div className="mb-6">
          <Link to="/" className="text-blue-600 hover:text-blue-800">
            ← Ana Sayfaya Dön
          </Link>
        </div>

        {/* Cihaz Bilgileri */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">📋 Cihaz Bilgileri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Temel Bilgiler</h3>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Platform:</span> {deviceInfo.platform}</div>
                <div><span className="font-medium">Dil:</span> {deviceInfo.language}</div>
                <div><span className="font-medium">Çevrimiçi:</span> {deviceInfo.onLine ? 'Evet' : 'Hayır'}</div>
                <div><span className="font-medium">Çekirdek:</span> {deviceInfo.hardwareConcurrency}</div>
                <div><span className="font-medium">Dokunma Noktası:</span> {deviceInfo.maxTouchPoints}</div>
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Cihaz Türü</h3>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">iOS:</span> {deviceInfo.isIOS ? 'Evet' : 'Hayır'}</div>
                <div><span className="font-medium">Android:</span> {deviceInfo.isAndroid ? 'Evet' : 'Hayır'}</div>
                <div><span className="font-medium">Mobil:</span> {deviceInfo.isMobile ? 'Evet' : 'Hayır'}</div>
                <div><span className="font-medium">HTTPS:</span> {deviceInfo.isHTTPS ? 'Evet' : 'Hayır'}</div>
                <div><span className="font-medium">Kamera:</span> {getStatusIcon(cameraStatus)} {getStatusText(cameraStatus)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Test Sonuçları */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🧪 Özellik Testleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Temel Özellikler</h3>
              <div className="space-y-1 text-sm">
                <div>{getStatusIcon(testResults.touch)} Dokunmatik Ekran</div>
                <div>{getStatusIcon(testResults.orientation)} Ekran Yönü</div>
                <div>{getStatusIcon(testResults.vibration)} Titreşim</div>
                <div>{getStatusIcon(testResults.geolocation)} Konum</div>
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Medya</h3>
              <div className="space-y-1 text-sm">
                <div>{getStatusIcon(testResults.audio)} Ses</div>
                <div>{getStatusIcon(testResults.video)} Video</div>
                <div>{getStatusIcon(testResults.canvas)} Canvas</div>
                <div>{getStatusIcon(deviceInfo.mediaDevices)} MediaDevices</div>
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Modern API'ler</h3>
              <div className="space-y-1 text-sm">
                <div>{getStatusIcon(testResults.fetch)} Fetch API</div>
                <div>{getStatusIcon(testResults.promises)} Promises</div>
                <div>{getStatusIcon(testResults.asyncAwait)} Async/Await</div>
                <div>{getStatusIcon(testResults.webGL)} WebGL</div>
              </div>
            </div>
          </div>
        </div>

        {/* QR Scanner Test */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">📷 QR Kod Tarayıcı Testi</h2>
          <div className="text-center">
            {cameraStatus === 'available' ? (
              <div>
                <p className="text-green-600 mb-4">✅ Kamera erişimi mevcut</p>
                <button
                  onClick={() => setShowQRScanner(true)}
                  className="btn-primary px-6 py-3"
                >
                  QR Tarayıcıyı Aç
                </button>
              </div>
            ) : cameraStatus === 'unavailable' ? (
              <div className="text-red-600">
                <p>❌ Kamera erişimi yok</p>
                <p className="text-sm mt-2">
                  {deviceInfo.isIOS && !deviceInfo.isHTTPS 
                    ? 'iOS cihazlarda HTTPS bağlantısı gereklidir'
                    : 'Tarayıcı ayarlarından kamera iznini kontrol edin'
                  }
                </p>
              </div>
            ) : (
              <div className="text-yellow-600">
                <p>⏳ Kamera durumu kontrol ediliyor...</p>
              </div>
            )}
          </div>
        </div>

        {/* iOS Özel Bilgiler */}
        {deviceInfo.isIOS && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-blue-900 mb-4">🍎 iOS Özel Bilgiler</h2>
            <div className="space-y-2 text-sm text-blue-800">
              <div>• Safari tarayıcısı önerilir</div>
              <div>• Kamera erişimi için HTTPS zorunlu</div>
              <div>• Otomatik oynatma kısıtlı</div>
              <div>• Kullanıcı etkileşimi gerekli</div>
              <div>• Ses seviyesi kontrolü sınırlı</div>
            </div>
          </div>
        )}

        {/* Android Özel Bilgiler */}
        {deviceInfo.isAndroid && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-green-900 mb-4">🤖 Android Özel Bilgiler</h2>
            <div className="space-y-2 text-sm text-green-800">
              <div>• Chrome tarayıcısı önerilir</div>
              <div>• Kamera erişimi desteklenir</div>
              <div>• Otomatik oynatma çalışır</div>
              <div>• Tam özellik desteği</div>
            </div>
          </div>
        )}

        {/* User Agent */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="font-medium text-gray-700 mb-2">User Agent</h3>
          <p className="text-xs text-gray-600 break-all">{deviceInfo.userAgent}</p>
        </div>
      </div>

      {/* QR Scanner Modal */}
      {showQRScanner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <QRScanner
              isOpen={showQRScanner}
              onScan={handleQRScan}
              onClose={() => setShowQRScanner(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileTest; 